// src/pages/seeker/SeekerWorkPreferences.jsx
// Work Preferences — auto-saves each field
// Route: /seeker/profile/work-preferences

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const C = {
  navy: '#0e4e95', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea',
};

// ── UI primitives ──────────────────────────────────────

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid var(--border)' }}>
    {children}
  </div>
);

const FL = ({ label, children, half, third, hint }) => (
  <div style={{ flex: third ? '1 1 160px' : half ? '1 1 200px' : '1 1 100%', minWidth: third ? '140px' : half ? '180px' : undefined, marginBottom: '14px' }}>
    <label className="form-label" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: 'var(--primary)', fontSize: '12px' }}>
      {label}{hint && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '6px' }}>{hint}</span>}
    </label>
    {children}
  </div>
);

const FS = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)} className="form-input" style={{ background: 'white' }}>
    <option value="">— select —</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// ── Autocomplete text input ────────────────────────────
const AutoInput = ({ value, onChange, onBlur, placeholder, suggestions }) => {
  const [show, setShow] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const ref = useRef();

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (v.trim()) {
      setFiltered(suggestions.filter(s => s.toLowerCase().includes(v.toLowerCase())).slice(0, 8));
      setShow(true);
    } else {
      setShow(false);
    }
  };

  const pick = (s) => { onChange(s); setShow(false); if (onBlur) onBlur(s); };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onBlur={() => { setTimeout(() => setShow(false), 150); if (onBlur) onBlur(value); }}
        placeholder={placeholder}
        className="form-input"
      />
      {show && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid var(--border)', borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
          {filtered.map((s, i) => (
            <div key={i} onMouseDown={() => pick(s)}
              style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}
              onMouseEnter={e => e.currentTarget.style.background = C.offwhite}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Benefits pill selector ─────────────────────────────
const BenefitsPicker = ({ selected, onChange }) => {
  const OPTIONS = [
    'Health Insurance', 'Dental', 'Vision', 'Life Insurance',
    'Superannuation / Pension', 'Paid Leave', 'Flexible Hours',
    'Remote Work', 'Work From Home Allowance', 'Gym / Wellness',
    'Professional Development', 'Study Leave', 'Stock Options / Equity',
    'Bonus / Performance Pay', 'Parental Leave', 'Childcare Support',
    'Employee Discounts', 'Company Car / Car Allowance', 'Phone Allowance',
    'Meal Allowance', 'Relocation Assistance', 'Visa Sponsorship',
  ];
  const toggle = (b) => {
    const next = selected.includes(b) ? selected.filter(x => x !== b) : [...selected, b];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {OPTIONS.map(b => {
        const active = selected.includes(b);
        return (
          <button key={b} type="button" onClick={() => toggle(b)}
            style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '20px', cursor: 'pointer', border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--border)', background: active ? 'var(--primary)' : 'white', color: active ? 'white' : 'var(--text-muted)', transition: 'all 0.15s' }}>
            {b}
          </button>
        );
      })}
    </div>
  );
};

// ── Suggestion lists ───────────────────────────────────
const LANGUAGES = ['English', 'Mandarin', 'Spanish', 'Hindi', 'Arabic', 'French', 'Bengali', 'Portuguese', 'Russian', 'Urdu', 'Indonesian', 'German', 'Japanese', 'Swahili', 'Marathi', 'Telugu', 'Turkish', 'Tamil', 'Vietnamese', 'Korean', 'Italian', 'Thai', 'Malay', 'Tagalog', 'Persian'];

const LOCATIONS = ['Remote', 'Sydney, NSW', 'Melbourne, VIC', 'Brisbane, QLD', 'Perth, WA', 'Adelaide, SA', 'Canberra, ACT', 'Hobart, TAS', 'Darwin, NT', 'Gold Coast, QLD', 'Newcastle, NSW', 'Auckland, NZ', 'Wellington, NZ', 'Singapore', 'Kuala Lumpur', 'Jakarta', 'Bangkok', 'Hong Kong', 'Tokyo', 'London', 'New York', 'San Francisco', 'Dubai'];

const INDUSTRIES = ['Accounting & Finance', 'Administration', 'Advertising & Marketing', 'Agriculture', 'Architecture & Design', 'Arts & Entertainment', 'Banking', 'Construction', 'Consulting', 'Customer Service', 'Defence & Military', 'Education & Training', 'Engineering', 'Government & Public Sector', 'Healthcare & Medical', 'Hospitality & Tourism', 'Human Resources', 'Information Technology', 'Insurance', 'Legal', 'Logistics & Supply Chain', 'Manufacturing', 'Media & Communications', 'Mining & Resources', 'Non-Profit', 'Pharmaceuticals', 'Real Estate', 'Retail & Consumer Goods', 'Sales', 'Science & Research', 'Sport & Fitness', 'Telecommunications', 'Transport', 'Utilities & Energy'];

// ══════════════════════════════════════════════════════
export const SeekerWorkPreferences = () => {
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  const [prefs, setPrefs] = useState({
    companySize:        '',
    workEnvironment:    '',
    workingLanguage:    '',
    preferredLocation:  '',
    benefits:           [],
    salaryMin:          '',
    salaryMax:          '',
    schedule:           '',
    industry:           '',
    employerType:       '',
  });

  const setP = (field) => (val) => setPrefs(p => ({ ...p, [field]: val }));

  // ── Save helpers ──────────────────────────────────────

  // Save the whole workPreferences JSON blob (debounced for text fields)
  const savePrefs = useCallback((nextPrefs) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await seekerAPI.updateProfile({ workPreferences: nextPrefs });
        toast.success('Saved', { duration: 1200, id: 'autosave' });
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Save failed');
      }
    }, 600);
  }, []);

  const immediateSet = (field) => (val) => {
    const next = { ...prefs, [field]: val };
    setPrefs(next);
    savePrefs(next);
  };

  const blurSet = (field) => (val) => {
    const next = { ...prefs, [field]: val };
    setPrefs(next);
    savePrefs(next);
  };

  // ── Load ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await seekerAPI.getProfile();
        const p = res.data.data;
        const wp = p.workPreferences || {};
        setPrefs({
          companySize:       wp.companySize       || '',
          workEnvironment:   wp.workEnvironment   || '',
          workingLanguage:   wp.workingLanguage   || '',
          preferredLocation: wp.preferredLocation || '',
          benefits:          Array.isArray(wp.benefits) ? wp.benefits : [],
          salaryMin:         wp.salaryMin         || '',
          salaryMax:         wp.salaryMax         || '',
          schedule:          wp.schedule          || '',
          industry:          wp.industry          || '',
          employerType:      wp.employerType      || '',
        });
      } catch { toast.error('Failed to load preferences'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: '40px', color: 'var(--text-muted)' }}>Loading…</div>;

  const card = { background: 'white', border: '1px solid var(--border)', borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Work Preferences</h1>
        <p>Changes are saved automatically.</p>
      </div>
    <div style={{ maxWidth: '720px' }}>

      {/* ── Workplace ── */}
      <div style={card}>
        <SectionTitle>Workplace</SectionTitle>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

          <FL label="Company Size" half>
            <FS value={prefs.companySize} onChange={immediateSet('companySize')} options={[
              { value: 'STARTUP',    label: 'Startup (1–20)' },
              { value: 'SMALL',      label: 'Small (21–100)' },
              { value: 'MEDIUM',     label: 'Medium (101–500)' },
              { value: 'LARGE',      label: 'Large (501–5000)' },
              { value: 'ENTERPRISE', label: 'Enterprise (5000+)' },
            ]} />
          </FL>

          <FL label="Workplace Environment" half>
            <FS value={prefs.workEnvironment} onChange={immediateSet('workEnvironment')} options={[
              { value: '',                label: 'No preference' },
              { value: 'OFFICE',          label: 'Office' },
              { value: 'TRANSPORTATION',  label: 'Transportation (car / truck / train / bus / boat etc.)' },
              { value: 'EDUCATIONAL',     label: 'Educational (schools / universities etc.)' },
              { value: 'RETAIL',          label: 'Retail (shopping centre, stores etc.)' },
              { value: 'HOSPITALITY',     label: 'Hospitality / Hotel' },
              { value: 'MANUFACTURING',   label: 'Manufacturing (factory)' },
              { value: 'UNIVERSITY',      label: 'University / school' },
              { value: 'GOVERNMENT',      label: 'Government' },
              { value: 'CONSTRUCTION',    label: 'Construction' },
              { value: 'OUTDOORS',        label: 'Outdoors' },
              { value: 'HEALTHCARE',      label: 'Healthcare (hospitals, medical centers etc.)' },
              { value: 'FARMING',         label: 'Farming / Agriculture' },
              { value: 'OTHER',           label: 'Other' },
            ]} />
          </FL>

          <FL label="Working Language" half>
            <AutoInput
              value={prefs.workingLanguage}
              onChange={setP('workingLanguage')}
              onBlur={blurSet('workingLanguage')}
              placeholder="e.g. English"
              suggestions={LANGUAGES}
            />
          </FL>

          <FL label="Preferred Location" half>
            <AutoInput
              value={prefs.preferredLocation}
              onChange={setP('preferredLocation')}
              onBlur={blurSet('preferredLocation')}
              placeholder="e.g. Sydney, NSW"
              suggestions={LOCATIONS}
            />
          </FL>

        </div>

        <FL label="Benefits">
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 8px' }}>Select all that matter to you.</p>
          <BenefitsPicker selected={prefs.benefits} onChange={(val) => immediateSet('benefits')(val)} />
        </FL>

      </div>

      {/* ── Position & Employer ── */}
      <div style={{ ...card, marginBottom: '40px' }}>
        <SectionTitle>Position & Employer</SectionTitle>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

          <FL label="Salary — Minimum" third hint="per year">
            <input type="number" min="0" step="1000"
              value={prefs.salaryMin}
              onChange={e => setP('salaryMin')(e.target.value)}
              onBlur={e => blurSet('salaryMin')(e.target.value)}
              placeholder="e.g. 60000"
              className="form-input" />
          </FL>

          <FL label="Salary — Maximum" third hint="per year">
            <input type="number" min="0" step="1000"
              value={prefs.salaryMax}
              onChange={e => setP('salaryMax')(e.target.value)}
              onBlur={e => blurSet('salaryMax')(e.target.value)}
              placeholder="e.g. 90000"
              className="form-input" />
          </FL>

          <FL label="Schedule" third>
            <FS value={prefs.schedule} onChange={immediateSet('schedule')} options={[
              { value: 'FULL_TIME',  label: 'Full-time' },
              { value: 'PART_TIME',  label: 'Part-time' },
              { value: 'CONTRACT',   label: 'Contract' },
              { value: 'CASUAL',     label: 'Casual' },
              { value: 'INTERNSHIP', label: 'Internship' },
            ]} />
          </FL>

          <FL label="Industry" half>
            <AutoInput
              value={prefs.industry}
              onChange={setP('industry')}
              onBlur={blurSet('industry')}
              placeholder="e.g. Information Technology"
              suggestions={INDUSTRIES}
            />
          </FL>

          <FL label="Employer Type" half>
            <FS value={prefs.employerType} onChange={immediateSet('employerType')} options={[
              { value: 'PRIVATE',     label: 'Private Sector' },
              { value: 'PUBLIC',      label: 'Public Sector / Government' },
              { value: 'NON_PROFIT',  label: 'Non-Profit / NGO' },
              { value: 'STARTUP',     label: 'Startup' },
              { value: 'AGENCY',      label: 'Recruitment Agency' },
              { value: 'NO_PREFERENCE', label: 'No Preference' },
            ]} />
          </FL>

        </div>
      </div>

    </div>
    </div>
  );
};
